import { useState, useMemo } from 'react';
import type { Alert } from '@/lib/stores/intelligence-store';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';

interface AlertsPanelProps {
  alerts: Alert[];
  onDismiss: (id: string) => void;
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'border-l-red-500 bg-red-50',
  warning: 'border-l-amber-500 bg-amber-50',
  info: 'border-l-cyan-500 bg-cyan-50',
};

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

function SeverityIcon({ severity }: { severity: string }): JSX.Element {
  if (severity === 'critical') {
    return (
      <svg className="h-4 w-4 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    );
  }
  if (severity === 'warning') {
    return (
      <svg className="h-4 w-4 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4 shrink-0 text-cyan-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  );
}

/**
 * Clinical alerts panel.
 * Displays alerts sorted by severity with dismiss functionality.
 */
export function AlertsPanel({ alerts, onDismiss }: AlertsPanelProps): JSX.Element {
  const [dismissedOpen, setDismissedOpen] = useState(false);

  const activeAlerts = useMemo(
    () =>
      alerts
        .filter((a) => !a.dismissed)
        .sort(
          (a, b) =>
            (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99),
        ),
    [alerts],
  );
  const dismissedAlerts = alerts.filter((a) => a.dismissed);

  return (
    <div className="card animate-fade-in">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-4">
        <h3 className="section-header">Alerts</h3>
        {activeAlerts.length > 0 && (
          <span className="badge-red flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse-glow" />
            <span className="font-mono text-2xs font-bold tabular-nums">
              {activeAlerts.length}
            </span>
          </span>
        )}
      </div>

      <div className="divider-gradient" />

      {/* ── Body ── */}
      <div className="p-5">
        {activeAlerts.length === 0 ? (
          /* ── Empty state ── */
          <div className="empty-state animate-scale-in">
            <div className="empty-state-icon">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <p className="empty-state-title">All clear</p>
            <p className="empty-state-desc">No active clinical alerts at this time</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {activeAlerts.map((alert, index) => (
              <div
                key={alert.id}
                className={`card rounded-lg border-l-[3px] p-4 ${
                  SEVERITY_STYLES[alert.severity] ?? 'border-l-slate-500'
                } ${
                  index === 0
                    ? 'animate-stagger-1'
                    : index === 1
                      ? 'animate-stagger-2'
                      : index === 2
                        ? 'animate-stagger-3'
                        : index === 3
                          ? 'animate-stagger-4'
                          : 'animate-stagger-5'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* ── Severity row ── */}
                    <div className="mb-2 flex items-center gap-2.5">
                      <SeverityIcon severity={alert.severity} />
                      <StatusBadge status={alert.severity} kind="severity" />
                      <span className="font-mono text-2xs font-medium text-slate-400 tracking-wide tabular-nums">
                        {alert.code}
                      </span>
                    </div>

                    {/* ── Message ── */}
                    <p className="text-[0.8125rem] leading-relaxed text-slate-700">
                      {alert.message}
                    </p>

                    {/* ── Timestamp ── */}
                    <p className="mt-1.5 font-mono text-2xs text-slate-400 tabular-nums tracking-wide">
                      {alert.at}
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDismiss(alert.id)}
                    className="shrink-0 text-slate-400 hover:text-slate-600"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="sr-only">Dismiss</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Dismissed alerts accordion ── */}
        {dismissedAlerts.length > 0 && (
          <div className="mt-5">
            <div className="divider-gradient mb-4" />
            <button
              onClick={() => setDismissedOpen((prev) => !prev)}
              className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-600"
            >
              <svg
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  dismissedOpen ? 'rotate-90' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="font-mono tabular-nums">
                {dismissedAlerts.length}
              </span>
              dismissed alert{dismissedAlerts.length !== 1 ? 's' : ''}
            </button>
            {dismissedOpen && (
              <div className="mt-2.5 animate-fade-in space-y-1.5 pl-2">
                {dismissedAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="flex items-center gap-2.5 rounded-lg bg-slate-50/80 px-3 py-2 opacity-50"
                  >
                    <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
                    <span className="font-mono text-2xs font-medium text-slate-400 uppercase tracking-wider">
                      {alert.severity}
                    </span>
                    <p className="flex-1 truncate text-xs text-slate-400">
                      {alert.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
